'use client'

import type { NotificationTypeEnum } from '@maestro/commerce-blocks/components/notification/notification.types'

import { useTranslations } from 'next-intl'
import { usePathname } from 'next/navigation'
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react'

import { getUserCart, loadCart } from '@maestro/commerce-blocks/client/api/cart'
import { emptyCartStructure } from '@maestro/commerce-blocks/components/cart/constants'
import { cartReducer } from '@maestro/commerce-blocks/context/cart-reducer'
import { useCartDispatch } from '@maestro/commerce-blocks/hooks/use-cart-dispatch'
import { useCartNotifications } from '@maestro/commerce-blocks/hooks/use-cart-notifications'
import { useCrossTabLock } from '@maestro/commerce-blocks/hooks/use-cross-tab-lock'
import { useCrossTabNotify } from '@maestro/commerce-blocks/hooks/use-cross-tab-notify'
import { useMaestroSession } from '@maestro/commerce-blocks/providers/maestro-session/maestro-session.context'
import {
  applyCouponCode,
  createCart,
  mergeGuestCartWithUserCart,
  removeCouponCode,
  selectStore as selectPointOfService,
  updateCartItems,
} from '@maestro/commerce-blocks/server/actions/cart'
import {
  cancelOrderEdit as cancelOrderEditAction,
  initiateOrderEdit as initiateOrderEditAction,
  updateOrder as updateOrderAction,
} from '@maestro/commerce-blocks/server/actions/order'
import {
  deleteCartIdFromStorage,
  getCartIdFromStorage,
  isCartIdActive,
  isGuestCart,
  isUserCart,
  saveCartIdToStorage,
} from '@maestro/commerce-blocks/session/cart-store'
import {
  deleteOrderEditSessionFromStorage,
  getOrderEditSessionFromStorage,
  type OrderEditSession,
  saveOrderEditSessionToStorage,
} from '@maestro/commerce-blocks/session/order-edit-store'
import { getProductQuantityLimit } from '@maestro/commerce-blocks/utils/product-stock.utils'
import { useA11y } from '@maestro/core/a11y/a11y.context'
import { useEventBusContext } from '@maestro/core/event/event-bus.context'
import { updateQueryIdTrackerCartId } from '@maestro/core/event/queryid-tracker.utils'
import { createLogger } from '@maestro/core/logger'
import {
  type Cart,
  type CartEntryStatus,
  type CartRecord,
  type EventPayloadType,
  EventType,
  type Order,
  type Price,
  type Product,
  type Store,
} from '@maestro/data-layer'
import { theme } from '@maestro/local/theme'

const log = createLogger('maestro:commerce-blocks:cart')

export const CartContext = createContext<CartContextProps>({
  isLoading: true,
  cart: { ...emptyCartStructure },
  deliveryPointOfService: undefined,
  cartEntryStatus: [],
  addProduct: () => null,
  addMultipleProducts: () => null,
  addProductsById: async () => false,
  updateCartEntry: () => null,
  emptyCart: () => null,
  emptyCartEntryStatus: () => null,
  mergeGuestCart: async () => {},
  loadUserCart: async () => {},
  createUserCart: async () => {},
  fetchCart: async () => {},
  resetCart: () => {},
  couponCodeApply: async () => false,
  couponCodeRemove: async () => {},
  deliveryPointOfServiceSelect: () => Promise.resolve(),
  resolveCartId: () => Promise.resolve(''),
  productHasStock: () => false,
  getAvailableQuantity: () => 0,
  isEditOrderSession: false,
  startOrderEdit: async () => {},
  saveOrderEdit: () => Promise.reject(new Error('Not implemented')),
  cancelOrderEdit: async () => {},
  getOriginalOrderQuantity: () => 0,
  hasPendingCartChanges: false,
})

export const useCartContext = () => {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useCart must be used within a CartContextProvider')
  }
  return context
}

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const t = useTranslations('commerce-blocks')
  const {
    notifyNotEnoughStock,
    notifySomeItemsOutOfStock,
    notifyCartCreateError,
    notifyItemFailedAdd,
    notifyItemFailedAddWithReason,
    notifyCartUpdated,
    notifyCartsMerged,
    notifyMergeFailed,
    notifyFallbackFailed,
    notifyLoadFailed,
    notifyCouponApplied,
    notifyCouponAlreadyApplied,
    notifyCouponApplyFailed,
    notifyCouponRemoved,
    notifyCouponRemoveFailed,
    notifyStoreSelectionError,
  } = useCartNotifications()

  const { session, isLoading: isSessionLoading } = useMaestroSession()
  const { publish } = useEventBusContext()
  const { announce } = useA11y()
  const { withLock: withCartMergeLock } = useCrossTabLock({
    name: 'cart_merge_in_progress',
  })

  const [context, dispatch] = useReducer(cartReducer, {
    cart: emptyCartStructure,
    records: [],
    cartEntryStatus: [],
    isLoading: true,
    notificationType: 'none',
  } as DispatchContext)

  const {
    dispatchAddProduct,
    dispatchAddMultipleProducts,
    dispatchUpdateCartEntry,
    dispatchEmptyCart,
    dispatchResetCart,
    dispatchResolveCart,
    dispatchPushPending,
    dispatchAddCartEntryStatus,
    dispatchEmptyCartEntryStatus,
  } = useCartDispatch({ dispatch })

  const [isMergingCarts, setIsMergingCarts] = useState(false)
  // Prevents multiple cart merge executions during a single user session
  // Resets to false on logout to allow merge on next login
  const hasAutoMergedRef = useRef(false)
  const hasFetchedCart = useRef(false)
  const pathname = usePathname()

  const [editOrderSession, setEditOrderSession] =
    useState<OrderEditSession | null>(() => getOrderEditSessionFromStorage())
  // Defaults true so a session already in storage at mount (e.g. after a
  // page reload while editing) is still eligible for cleanup right away.
  const hasReachedCartRef = useRef(true)
  const isEditOrderSession = Boolean(editOrderSession)

  const getCustomerId = useCallback(() => {
    return session?.authContext?.userId
  }, [session?.authContext?.userId])
  const customerIdRef = useRef(getCustomerId())

  const getAvailableQuantity = useCallback(
    (product: Product, variantId: string) => {
      if (!theme().product.isAddToCartProductStockValidationEnabled) {
        // If check is disabled assume the product always in stock
        return theme().cms.quantityCounterMaxValue
      }

      const quantityLimit = getProductQuantityLimit(product, variantId)

      const existingItem = context.cart.entries.find((entry) => {
        return entry.productId === product.id && entry.variantId === variantId
      })

      return Math.max(0, quantityLimit - (existingItem?.quantity ?? 0))
    },
    [context.cart.entries]
  )

  const productHasStock = useCallback(
    (product: Product, variantId: string) => {
      return getAvailableQuantity(product, variantId) >= 1
    },
    [getAvailableQuantity]
  )

  const emptyCartEntryStatus = useCallback(() => {
    dispatchEmptyCartEntryStatus()
  }, [dispatchEmptyCartEntryStatus])

  const addProduct = useCallback(
    (
      product: Product,
      variantId: string,
      notificationType: NotificationTypeEnum = 'regular'
    ) => {
      if (productHasStock(product, variantId)) {
        dispatchAddProduct(product, variantId, notificationType)
        dispatchAddCartEntryStatus({ productId: product.id, status: 'add' })
      } else {
        notifyNotEnoughStock()
      }
    },
    [
      dispatchAddProduct,
      productHasStock,
      notifyNotEnoughStock,
      dispatchAddCartEntryStatus,
    ]
  )

  const addMultipleProducts = useCallback(
    (
      products: {
        product: Product
        variantId: string
        quantity: number
      }[],
      notificationType: NotificationTypeEnum = 'regular'
    ) => {
      const productsWithStock = products.filter(
        ({ product, variantId, quantity }) =>
          getAvailableQuantity(product, variantId) >= quantity
      )

      if (productsWithStock.length === 0) {
        notifyNotEnoughStock()
        return
      }

      dispatchAddMultipleProducts(productsWithStock, notificationType)

      if (productsWithStock.length < products.length) {
        notifySomeItemsOutOfStock()
      }
    },
    [
      dispatchAddMultipleProducts,
      getAvailableQuantity,
      notifyNotEnoughStock,
      notifySomeItemsOutOfStock,
    ]
  )

  const updateCartEntry = useCallback(
    (
      cartEntryId: string,
      productId: string,
      variantId: string,
      quantity: number,
      notificationType: NotificationTypeEnum = 'regular'
    ) => {
      if (quantity === 0) {
        dispatchAddCartEntryStatus({
          productId: productId,
          status: 'removeAll',
        })
      }
      dispatchUpdateCartEntry(
        cartEntryId,
        productId,
        variantId,
        quantity,
        notificationType
      )
    },
    [dispatchAddCartEntryStatus, dispatchUpdateCartEntry]
  )

  const emptyCart = useCallback(() => {
    // Capture cart state before emptying
    const itemsBeforeEmpty: EventPayloadType[EventType.REMOVED_ALL_PRODUCTS_FROM_CART]['items'] =
      context.cart.entries.map((entry) => {
        const product: Product = {
          name: entry.productName,
          gallery: [],
          urlPath: entry.urlPath,
          variantAttributes: [],
          variants: [],
          id: entry.productId,
          defaultVariantId: entry.productId,
          price: entry.price,
          image: entry.image,
          productType: entry.productType,
        }

        return {
          product,
          quantity: entry.quantity,
        }
      })

    // Dispatch state change
    dispatchEmptyCart()

    // Publish event with items before removal and empty cart after
    const payload: EventPayloadType[EventType.REMOVED_ALL_PRODUCTS_FROM_CART] =
      {
        items: itemsBeforeEmpty,
        cart: [],
        location: 'cart',
      }

    publish<EventPayloadType[EventType.REMOVED_ALL_PRODUCTS_FROM_CART]>(
      EventType.REMOVED_ALL_PRODUCTS_FROM_CART,
      payload
    )
    announce(t('cart.remove-all-products'), 'assertive')
  }, [announce, context.cart.entries, publish, t, dispatchEmptyCart])

  const resetCart = useCallback(() => {
    deleteCartIdFromStorage()
    dispatchResetCart(emptyCartStructure)
  }, [dispatchResetCart])

  const fetchCart = useCallback(async () => {
    const cartCurrent = getCartIdFromStorage()
    const customerId = getCustomerId()

    // Skip loading cart when merge is in progress or when merge should happen
    if (isMergingCarts) {
      return
    }

    // If user just logged in and has guest cart, let login useEffect handle merge
    if (customerId && cartCurrent?.cartId && isGuestCart(cartCurrent)) {
      return
    }

    if (isCartIdActive(cartCurrent)) {
      // Check if cart DON'T belongs to current user (for logged in users)
      // Skip guest carts as they are handled by merge logic instead
      if (
        customerId &&
        !isUserCart(cartCurrent, customerId) &&
        !isGuestCart(cartCurrent)
      ) {
        // Cart belongs to different user, reset and load user cart instead
        resetCart()
        return
      }

      try {
        const cart = await loadCart(cartCurrent.cartId)
        if (cart.cartId) {
          saveCartIdToStorage(cart)
          dispatchResetCart(cart)
        } else {
          resetCart()
        }
      } catch (error) {
        // If the cart cannot be loaded (e.g. cart not found), reset
        log.warn('Failed to load cart, resetting:', error)
        resetCart()
      }
    } else {
      resetCart()
    }
  }, [getCustomerId, isMergingCarts, resetCart, dispatchResetCart])

  const getOriginalOrderQuantity = useCallback(
    (code: string): number => {
      return editOrderSession?.originalQuantities[code] ?? 0
    },
    [editOrderSession]
  )

  const startOrderEdit = useCallback(
    async (orderId: string) => {
      const previousCartId = getCartIdFromStorage()?.cartId ?? ''
      const { cartId, originalQuantities } =
        await initiateOrderEditAction(orderId)

      hasReachedCartRef.current = false
      const session: OrderEditSession = {
        orderId,
        previousCartId,
        originalQuantities,
      }
      saveOrderEditSessionToStorage(session)
      setEditOrderSession(session)

      const cart = await loadCart(cartId)
      if (cart.cartId) {
        saveCartIdToStorage(cart)
        dispatchResetCart(cart)
      }
    },
    [dispatchResetCart]
  )

  const cancelOrderEdit = useCallback(async () => {
    if (!editOrderSession) {
      return
    }

    const cartCurrent = getCartIdFromStorage()
    try {
      if (cartCurrent?.cartId) {
        await cancelOrderEditAction(cartCurrent.cartId)
      }
    } finally {
      deleteOrderEditSessionFromStorage()
      setEditOrderSession(null)
    }

    const previousCart = editOrderSession.previousCartId
      ? await loadCart(editOrderSession.previousCartId)
      : null
    if (previousCart?.cartId) {
      saveCartIdToStorage(previousCart)
      dispatchResetCart(previousCart)
      return
    }

    // No cart was cached client-side when the edit started (e.g. navigated
    // straight from order-history) — check the server for the customer's
    // real active cart before falling back to an empty one.
    const customerId = getCustomerId()
    const existingUserCart = customerId ? await getUserCart() : null
    if (existingUserCart?.cartId) {
      saveCartIdToStorage(existingUserCart)
      dispatchResetCart(existingUserCart)
    } else {
      resetCart()
    }
  }, [editOrderSession, dispatchResetCart, resetCart, getCustomerId])

  const saveOrderEdit = useCallback(async (): Promise<Order> => {
    if (!editOrderSession) {
      throw new Error(t('cart.edit-order-no-session'))
    }

    const cartCurrent = getCartIdFromStorage()
    const updatedOrder = await updateOrderAction(
      editOrderSession.orderId,
      cartCurrent?.cartId ?? ''
    )

    deleteOrderEditSessionFromStorage()
    setEditOrderSession(null)

    const previousCart = editOrderSession.previousCartId
      ? await loadCart(editOrderSession.previousCartId)
      : null
    if (previousCart?.cartId) {
      saveCartIdToStorage(previousCart)
      dispatchResetCart(previousCart)
      return updatedOrder
    }

    // No cart was cached client-side when the edit started (e.g. navigated
    // straight from order-history) — check the server for the customer's
    // real active cart before falling back to an empty one.
    const customerId = getCustomerId()
    const existingUserCart = customerId ? await getUserCart() : null
    if (existingUserCart?.cartId) {
      saveCartIdToStorage(existingUserCart)
      dispatchResetCart(existingUserCart)
    } else {
      resetCart()
    }

    return updatedOrder
  }, [editOrderSession, resetCart, dispatchResetCart, getCustomerId, t])

  useEffect(() => {
    if (!editOrderSession) {
      return
    }
    if (pathname === theme().urls.cart) {
      hasReachedCartRef.current = true
      return
    }
    // Only treat leaving to these pages as abandoning the edit — product/
    // category/search pages stay allowed so adding another item mid-edit
    // doesn't cancel the session.
    // TODO: hardcoded allowlist, not derived from routing config — re-check
    // if new "exit" pages get added (out of scope for MAES-1911).
    const isAbandonPath =
      pathname === theme().urls.orders ||
      pathname === theme().urls.myAccount ||
      pathname === theme().urls.checkout ||
      pathname === theme().urls.home ||
      (pathname?.startsWith('/order/') ?? false)
    // hasReachedCartRef avoids firing while startOrderEdit's own navigation
    // to /cart is still in flight.
    if (hasReachedCartRef.current && isAbandonPath) {
      void cancelOrderEdit()
    }
  }, [pathname, editOrderSession, cancelOrderEdit])

  const createUserCart = useCallback(async () => {
    const customerId = getCustomerId()
    try {
      const cartCurrent = await createCart(customerId)
      saveCartIdToStorage(cartCurrent)
      updateQueryIdTrackerCartId(cartCurrent.cartId)
      dispatchResetCart({ ...emptyCartStructure, ...cartCurrent })
    } catch (error) {
      notifyCartCreateError()
      log.error('Error creating cart:', error)
    }
  }, [getCustomerId, notifyCartCreateError, dispatchResetCart])

  useEffect(() => {
    // Skip loading cart when session is loading or when user is on checkout success page
    if (
      isSessionLoading ||
      hasFetchedCart.current ||
      pathname?.includes(theme().urls?.checkoutSuccess)
    ) {
      return
    }
    hasFetchedCart.current = true

    void fetchCart()
  }, [fetchCart, isSessionLoading, pathname])

  const { notify: notifyOtherTabsCartUpdated } = useCrossTabNotify({
    channel: 'cart_merged',
    onNotified: async () => {
      log.debug('Received cart merge notification from another tab, refetching')
      await fetchCart()
    },
  })

  const resolveCartInflightRef = useRef<Promise<Cart> | null>(null)

  /**
   * Cart is created lazily — we don't create a cart until it's needed.
   * This ensures a cart exists and returns cart, creating one if necessary.
   * Multiple step process to handle various edge cases (e.g. cart in storage but invalid, merge guest cart on login, etc.) while ensuring only one cart is created and used at a time.
   * Concurrency safe: if multiple calls happen simultaneously, they will wait for the same inflight promise instead of creating multiple carts.
   */
  const resolveCart = useCallback(async (): Promise<Cart> => {
    if (context.cart.cartId) {
      return context.cart
    }

    // Concurrency guard - reuse inflight promise
    if (resolveCartInflightRef.current) {
      return resolveCartInflightRef.current
    }

    const promise = (async () => {
      const current = getCartIdFromStorage()
      let cart: Cart | null = null
      if (isCartIdActive(current)) {
        try {
          cart = await loadCart(current.cartId)
        } catch {
          log.warn('Stored cart no longer valid, getting new one')
        }
      } else {
        deleteCartIdFromStorage()
      }

      const customerId = getCustomerId()

      if (!cart?.cartId && customerId) {
        try {
          cart = await getUserCart()
        } catch {
          log.warn('User cart not retrieved, creating new one')
        }
      }

      if (!cart?.cartId) {
        try {
          const cartData = await createCart(customerId)
          if (cartData.cartId) {
            cart = { ...emptyCartStructure, ...cartData }
          }
        } catch {
          log.warn('Cart not created, using empty cart structure')
        }
      }

      if (!cart?.cartId) {
        notifyCartCreateError()
        throw new Error('Failed to resolve cart: no cartId after create')
      }
      saveCartIdToStorage(cart)
      dispatchResolveCart({ ...emptyCartStructure, ...cart })
      return cart
    })()

    resolveCartInflightRef.current = promise
    try {
      return await promise
    } finally {
      resolveCartInflightRef.current = null
    }
  }, [context.cart, getCustomerId, dispatchResolveCart, notifyCartCreateError])

  const resolveCartId = useCallback(async (): Promise<string> => {
    const cart = await resolveCart()
    return cart.cartId
  }, [resolveCart])

  /**
   * Adds items by product/variant ID without requiring full Product data.
   * Uses the standard cart flow and optionally validates stock when
   * `options.validateStock` is enabled and `product` data is provided.
   */
  const addProductsById = useCallback(
    async (
      items: {
        productId: string
        variantId: string
        quantity: number
        product?: Product
      }[],
      notificationType: NotificationTypeEnum = 'regular',
      options?: { validateStock?: boolean }
    ): Promise<boolean> => {
      if (!items.length) {
        return false
      }

      let itemsToAdd = items

      if (options?.validateStock) {
        itemsToAdd = items.filter(
          ({ product, variantId, quantity }) =>
            !product || getAvailableQuantity(product, variantId) >= quantity
        )

        if (itemsToAdd.length === 0) {
          notifyNotEnoughStock()
          return false
        }

        if (itemsToAdd.length < items.length) {
          notifySomeItemsOutOfStock()
        }
      }

      let cartCurrent: Cart
      try {
        cartCurrent = await resolveCart()
      } catch (error) {
        log.error('Error resolving cart before adding items:', error)
        notifyCartCreateError()
        return false
      }

      dispatchPushPending()

      const records: CartRecord[] = itemsToAdd.map(
        ({ productId, variantId, quantity }) => ({
          productId,
          variantId,
          quantity,
        })
      )

      let success = false

      try {
        const result = await updateCartItems({
          cartId: cartCurrent.cartId,
          cartVersion: cartCurrent.cartVersion,
          records,
        })

        if (result.success) {
          success = true
          notifyCartUpdated(
            notificationType,
            'added',
            result.cartLight?.priceSummary?.grandTotal,
            result.cartLight?.totalQuantity
          )
        } else if (result.message) {
          notifyItemFailedAddWithReason(result.message)
        } else {
          notifyItemFailedAdd()
        }
      } catch (error) {
        notifyItemFailedAdd()
        log.error('Error adding items to cart:', error)
      } finally {
        try {
          const updatedCart = await loadCart(cartCurrent.cartId)
          if (updatedCart.cartId) {
            saveCartIdToStorage(updatedCart)
            dispatchResetCart(updatedCart)
          }
        } catch {
          dispatchResetCart(context.cart)
        }
      }

      return success
    },
    [
      context.cart,
      dispatchPushPending,
      dispatchResetCart,
      getAvailableQuantity,
      notifyCartCreateError,
      notifyCartUpdated,
      notifyItemFailedAdd,
      notifyItemFailedAddWithReason,
      notifyNotEnoughStock,
      notifySomeItemsOutOfStock,
      resolveCart,
    ]
  )

  const lastCallIdRef = useRef(0)
  const updateQueueRef = useRef<Promise<void>>(Promise.resolve())

  useEffect(() => {
    const { records, notificationType } = context

    if (!records.length || isMergingCarts) {
      return
    }

    const callId = ++lastCallIdRef.current

    const timer = setTimeout(() => {
      // Chain the async operation to the queue to ensure strict sequential execution
      updateQueueRef.current = updateQueueRef.current
        .then(async () => {
          // Skip this if user updates cart under the debounce time
          if (callId !== lastCallIdRef.current) {
            dispatchEmptyCartEntryStatus()
            return
          }

          let cartCurrent: Cart | null = null
          try {
            cartCurrent = await resolveCart()
          } catch (e) {
            log.error('Error resolving cart ID:', e)
            // reset to the empty cart to drop any the peniding records when cart id failed to be created
            dispatchEmptyCartEntryStatus()
            dispatchResetCart()
            return
          }

          dispatchPushPending()

          let cart: Cart | null = null
          let shouldCommit = true

          try {
            const result = await updateCartItems({
              cartId: cartCurrent.cartId,
              cartVersion: cartCurrent.cartVersion,
              records,
            })
            if (callId !== lastCallIdRef.current) {
              dispatchEmptyCartEntryStatus()
              // A newer call has been made, discard this result
              shouldCommit = false
              return
            }

            let itemsRemoved = false
            let totalQuantity = 0
            let totalPrice: Price | null = null

            if (result.success) {
              cart = result.cart
              if (!cart) {
                const cartLight = result.cartLight
                itemsRemoved =
                  cartLight.totalQuantity < context.cart.totalQuantity
                totalQuantity = cartLight.totalQuantity
                totalPrice = cartLight?.priceSummary?.grandTotal
              } else {
                itemsRemoved = cart.totalQuantity < context.cart.totalQuantity
                totalQuantity = cart.totalQuantity
                totalPrice = cart?.priceSummary?.grandTotal
              }
              notifyCartUpdated(
                notificationType,
                itemsRemoved ? 'removed' : 'added',
                totalPrice,
                totalQuantity
              )
              dispatchEmptyCartEntryStatus()
            } else {
              if (result.message) {
                notifyItemFailedAddWithReason(result.message)
                dispatchAddCartEntryStatus({
                  productId: result.productId,
                  status: result.message,
                })
              } else {
                notifyItemFailedAdd()
              }
            }
          } catch (error) {
            notifyItemFailedAdd()
            dispatchEmptyCartEntryStatus()
            log.error('Error updating cart:', error)
          } finally {
            if (shouldCommit) {
              try {
                const updatedCart = cart ?? (await loadCart(cartCurrent.cartId))
                if (updatedCart.cartId) {
                  saveCartIdToStorage(updatedCart)
                  dispatchResetCart(updatedCart)
                }
              } catch {
                dispatchResetCart(context.cart)
              }
            }
          }
        })
        .catch((err) => {
          log.error('Queue execution error:', err)
        })
    }, theme().cart.bounce)

    return () => {
      clearTimeout(timer)
    }
  }, [
    context,
    getCustomerId,
    isMergingCarts,
    notifyCartUpdated,
    notifyItemFailedAdd,
    notifyItemFailedAddWithReason,
    dispatchPushPending,
    dispatchResetCart,
    dispatchAddCartEntryStatus,
    resolveCart,
    dispatchEmptyCartEntryStatus,
  ])

  const persistCartAndResetState = useCallback(
    (cart: Cart, customerId?: string) => {
      const cartWithCustomer = customerId ? { ...cart, customerId } : cart
      saveCartIdToStorage(cartWithCustomer)
      dispatchResetCart(cart)
    },
    [dispatchResetCart]
  )

  const mergeGuestCart = useCallback(
    async (customerId?: string) => {
      await withCartMergeLock(async () => {
        setIsMergingCarts(true)

        try {
          const guestCartData = getCartIdFromStorage()
          const existingUserCart = customerId ? await getUserCart() : null

          // No guest cart OR cart already belongs to this user - setup user cart
          if (!guestCartData?.cartId || isUserCart(guestCartData, customerId)) {
            if (existingUserCart) {
              persistCartAndResetState(existingUserCart, customerId)
            }
            // Don't create empty cart - will be created when first product is added
            return
          }

          // Merge guest cart with user cart
          dispatchPushPending()

          const mergedCart = await mergeGuestCartWithUserCart({
            guestCartId: guestCartData.cartId,
            userCartId: existingUserCart?.cartId,
            userCartGuid: existingUserCart?.userCartGuid,
            userCartVersion: existingUserCart?.cartVersion,
            customerId,
          })

          if (mergedCart.cartId) {
            deleteCartIdFromStorage()
            updateQueryIdTrackerCartId(mergedCart.cartId)
            persistCartAndResetState(mergedCart, customerId)
            notifyOtherTabsCartUpdated()

            if (
              isGuestCart(guestCartData) &&
              guestCartData.totalQuantity !== mergedCart.totalQuantity
            ) {
              notifyCartsMerged()
            }
          }
        } catch (e) {
          log.error('Error in cart merge:', e)
          notifyMergeFailed()

          // Fallback: Try to recover with user cart or create new one
          try {
            const existingUserCart = customerId ? await getUserCart() : null
            if (existingUserCart) {
              persistCartAndResetState(existingUserCart, customerId)
            }
          } catch {
            notifyFallbackFailed()
          }
        } finally {
          setIsMergingCarts(false)
        }
      })
    },
    [
      withCartMergeLock,
      notifyOtherTabsCartUpdated,
      notifyCartsMerged,
      notifyFallbackFailed,
      notifyMergeFailed,
      persistCartAndResetState,
      dispatchPushPending,
    ]
  )

  const loadUserCart = useCallback(
    async (customerId: string) => {
      setIsMergingCarts(true)
      try {
        dispatchPushPending()

        const existingUserCart = await getUserCart()
        if (existingUserCart) {
          persistCartAndResetState(existingUserCart, customerId)
        } else {
          dispatchResetCart()
        }
      } catch (error) {
        log.error('Error loading user cart:', error)
        notifyLoadFailed()
      } finally {
        setIsMergingCarts(false)
      }
    },
    [
      dispatchPushPending,
      dispatchResetCart,
      notifyLoadFailed,
      persistCartAndResetState,
    ]
  )

  // Handle cart operations when user logs in
  useEffect(() => {
    const customerId = getCustomerId()
    const checkoutSuccess = theme().urls?.checkoutSuccess ?? '/checkout/success'
    const isCheckoutSuccessPath = pathname?.includes(checkoutSuccess) ?? false

    if (customerId && !hasAutoMergedRef.current && !isCheckoutSuccessPath) {
      hasAutoMergedRef.current = true

      const initCart = async () => {
        const currentCartData = getCartIdFromStorage()

        try {
          if (currentCartData?.cartId) {
            if (
              isGuestCart(currentCartData) ||
              !isUserCart(currentCartData, customerId)
            ) {
              await mergeGuestCart(customerId)
            }
          } else {
            await loadUserCart(customerId)
          }
        } catch (error) {
          log.error('Error initializing user cart:', error)
        }
      }
      void initCart()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCustomerId, loadUserCart, mergeGuestCart]) //dont add pathname to deps, we only want this to run on login, not on path change

  // Reset cart state when user logs out
  useEffect(() => {
    if (!context.isLoading) {
      const customerId = getCustomerId()

      // run once after user logged off
      if (customerIdRef.current && !customerId) {
        hasAutoMergedRef.current = false
        dispatchResetCart(emptyCartStructure)
        deleteOrderEditSessionFromStorage()
        setEditOrderSession(null)
      }
      customerIdRef.current = customerId
    }
  }, [context.isLoading, getCustomerId, dispatchResetCart])

  const couponCodeApply = useCallback(
    async (code: string) => {
      try {
        const cartCurrent = getCartIdFromStorage()
        if (!cartCurrent?.cartId) {
          throw new Error('No active cart found')
        }
        const result = await applyCouponCode({
          cartId: cartCurrent.cartId,
          cartVersion: cartCurrent.cartVersion,
          code,
          customerId: cartCurrent.customerId,
        })

        if (result.success) {
          saveCartIdToStorage(result.cart)
          dispatchResetCart(result.cart)
          notifyCouponApplied()
          return true
        }

        if (result.errorCode === 'DUPLICATE_COUPON') {
          notifyCouponAlreadyApplied()
        } else {
          notifyCouponApplyFailed(code)
        }
        return false
      } catch {
        notifyCouponApplyFailed(code)
        return false
      }
    },
    [
      notifyCouponAlreadyApplied,
      notifyCouponApplied,
      notifyCouponApplyFailed,
      dispatchResetCart,
    ]
  )

  const couponCodeRemove = useCallback(
    async (code: string, codeId: string) => {
      try {
        const cartCurrent = getCartIdFromStorage()
        if (!cartCurrent?.cartId) {
          throw new Error('No active cart found')
        }
        const cartUpdated = await removeCouponCode({
          cartId: cartCurrent.cartId,
          cartVersion: cartCurrent.cartVersion,
          code,
          codeId,
          customerId: cartCurrent.customerId,
        })
        saveCartIdToStorage(cartUpdated)
        dispatchResetCart(cartUpdated)
        notifyCouponRemoved()
      } catch {
        notifyCouponRemoveFailed(code)
      }
    },
    [notifyCouponRemoved, notifyCouponRemoveFailed, dispatchResetCart]
  )

  const deliveryPointOfServiceSelect = useCallback(
    async (store: Store) => {
      try {
        const cartId = await resolveCartId()
        const cartCurrent = getCartIdFromStorage()

        const cartUpdated = await selectPointOfService({
          cartId,
          cartVersion: cartCurrent.cartVersion,
          storeName: store.id,
          customerId: cartCurrent.customerId,
        })
        saveCartIdToStorage(cartUpdated)
        dispatchResetCart(cartUpdated)
      } catch {
        notifyStoreSelectionError(store.name)
      }
    },
    [resolveCartId, notifyStoreSelectionError, dispatchResetCart]
  )

  const value = {
    addMultipleProducts,
    addProduct,
    addProductsById,
    cart: context.cart,
    cartEntryStatus: context.cartEntryStatus,
    couponCodeApply,
    couponCodeRemove,
    deliveryPointOfService: context.cart.deliveryPointOfService,
    deliveryPointOfServiceSelect,
    emptyCart,
    emptyCartEntryStatus,
    resolveCartId,
    fetchCart,
    resetCart,
    isLoading: context.isLoading,
    loadUserCart,
    createUserCart,
    mergeGuestCart,
    productHasStock,
    getAvailableQuantity,
    updateCartEntry,
    isEditOrderSession,
    startOrderEdit,
    saveOrderEdit,
    cancelOrderEdit,
    getOriginalOrderQuantity,
    hasPendingCartChanges: !!context.records.length || context.isLoading,
  }

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

interface CartContextProps {
  readonly cart: Cart
  readonly isLoading: boolean
  readonly cartEntryStatus: CartEntryStatus[]
  readonly deliveryPointOfService: Store | undefined
  readonly addProduct: (
    product: Product,
    variantId: string,
    notificationType?: NotificationTypeEnum
  ) => void
  readonly addMultipleProducts: (
    products: {
      product: Product
      variantId: string
      quantity: number
    }[],
    notificationType?: NotificationTypeEnum
  ) => void
  readonly addProductsById: (
    items: {
      productId: string
      variantId: string
      quantity: number
      product?: Product
    }[],
    notificationType?: NotificationTypeEnum,
    options?: { validateStock?: boolean }
  ) => Promise<boolean>
  readonly updateCartEntry: (
    cartEntryId: string,
    productId: string,
    variantId: string,
    quantity: number,
    notificationType?: NotificationTypeEnum
  ) => void
  readonly emptyCart: () => void
  readonly emptyCartEntryStatus: () => void
  readonly mergeGuestCart: (customerId?: string) => Promise<void>
  readonly loadUserCart: (customerId: string) => Promise<void>
  readonly createUserCart: () => Promise<void>
  readonly fetchCart: () => Promise<void>
  readonly resetCart: () => void
  readonly couponCodeApply: (code: string) => Promise<boolean>
  readonly couponCodeRemove: (code: string, codeId: string) => Promise<void>
  readonly deliveryPointOfServiceSelect: (store: Store) => Promise<void>
  readonly resolveCartId: () => Promise<string>
  readonly productHasStock: (product: Product, variantId: string) => boolean
  readonly getAvailableQuantity: (product: Product, variantId: string) => number
  readonly isEditOrderSession: boolean
  readonly startOrderEdit: (orderId: string) => Promise<void>
  readonly saveOrderEdit: () => Promise<Order>
  readonly cancelOrderEdit: () => Promise<void>
  readonly getOriginalOrderQuantity: (code: string) => number
  readonly hasPendingCartChanges: boolean
}

export interface DispatchContext {
  cart: Cart
  records: CartRecord[]
  isLoading: boolean
  cartEntryStatus: CartEntryStatus[]
  notificationType: NotificationTypeEnum
}
